/** Canonical public site URL (set VITE_SITE_URL in production, e.g. https://www.yourdomain.com). */
export function getSiteUrl(): string {
  const v = import.meta.env.VITE_SITE_URL as string | undefined;
  if (v && /^https?:\/\//i.test(v.trim())) return v.trim().replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://leisurebreaks.co.za';
}

export function absoluteUrl(pathOrUrl: string): string {
  if (!pathOrUrl) return getSiteUrl();
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = getSiteUrl();
  const p = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${p}`;
}

export const SITE_NAME = 'Leisure Breaks';

export const DEFAULT_DESCRIPTION =
  'Book your perfect getaway with Leisure Breaks - premium vacation rentals across South Africa.';

export function stripHtmlForMeta(html: string, maxLen = 160): string {
  const plain = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen - 1).trim()}…`;
}

function upsertMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLinkRel(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.type = 'application/ld+json';
  el.textContent = JSON.stringify(data);
}

export interface PageSeoInput {
  title: string;
  description?: string;
  /** Path only, e.g. /search - used for canonical */
  path: string;
  image?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function applyPageSeo(input: PageSeoInput): void {
  if (typeof document === 'undefined') return;

  const description = (input.description || DEFAULT_DESCRIPTION).slice(0, 320);
  const canonical = absoluteUrl(input.path.startsWith('/') ? input.path : `/${input.path}`);
  const image = input.image ? absoluteUrl(input.image) : absoluteUrl('/brand/Company Logo/leisure-breaks-logo-web-version-1000-with-shadow-1-768x448.png');

  document.title = input.title.includes(SITE_NAME) ? input.title : `${input.title} | ${SITE_NAME}`;

  upsertMetaName('description', description);
  upsertLinkRel('canonical', canonical);

  upsertMetaProperty('og:type', input.ogType || 'website');
  upsertMetaProperty('og:site_name', SITE_NAME);
  upsertMetaProperty('og:title', input.title);
  upsertMetaProperty('og:description', description);
  upsertMetaProperty('og:url', canonical);
  upsertMetaProperty('og:image', image);
  upsertMetaProperty('og:locale', 'en_ZA');

  upsertMetaName('twitter:card', 'summary_large_image');
  upsertMetaName('twitter:title', input.title);
  upsertMetaName('twitter:description', description);
  upsertMetaName('twitter:image', image);

  if (input.noIndex) {
    upsertMetaName('robots', 'noindex, nofollow');
  } else {
    upsertMetaName('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  }

  if (input.jsonLd != null) {
    const payload = Array.isArray(input.jsonLd) ? input.jsonLd : [input.jsonLd];
    upsertJsonLd('seo-jsonld-page', { '@context': 'https://schema.org', '@graph': payload });
  } else {
    const el = document.getElementById('seo-jsonld-page');
    if (el) el.remove();
  }
}

/** Site-wide graph: Organization + WebSite (call once from public layout). */
export function applySiteJsonLd(): void {
  if (typeof document === 'undefined') return;
  const url = getSiteUrl();
  upsertJsonLd('seo-jsonld-site', {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${url}/#organization`,
        name: SITE_NAME,
        url,
        logo: absoluteUrl('/brand/Company Logo/leisure-breaks-logo-web-version-1000-with-shadow-1-400x314.png'),
        sameAs: [] as string[],
      },
      {
        '@type': 'WebSite',
        '@id': `${url}/#website`,
        url,
        name: SITE_NAME,
        description: DEFAULT_DESCRIPTION,
        publisher: { '@id': `${url}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${url}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  });
}
